<template>
  <AppView
    :is-mocked-mode="isMockedMode"
    :show-customisation-loader="showCustomisationLoader"
    :show-float-button="showFloatButton"
    :show-my-request="showMyRequest"
    :show-article="showArticle"
    :show-article-id="showArticleId"
    :article-title="articleTitle"
    :category-name="categoryName"
    :article-body="articleBody"
    :article-section-title="articleSectionTitle"
    :fetch-article="fetchArticle"
    :is-from-autocomplete="isFromAutocomplete"
    :article-use-config="articleUseConfig"
    :show-router-view="showRouterView"
    :show-chat-widget="showChatWidget"
    @handle-customisation-retry="handleCustomisationRetry"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { platform, Bridge, getUrlWithProtocol } from '@/common/bridge';
import * as CommsService from '@/common/bridge/modern-bridge/comms.service';
import { CommsCommand } from '@/common/bridge/modern-bridge/comms.enum';
import { onOpenMyRequest, onOpenArticle, onFloatButton, emitFloatButton } from '@/common/eventBus';
import AppView from '@/views/AppView';
import { CustomerServiceProvider } from '@/common/config';
import { InitializeOptions, CustomisationUpdatePayload } from './common/types';
import { ConnectToAgent } from './common/bot/types';
import { store as vuexStore } from './store';
import { analytics } from './common/logs/EventLogger';
import { GENERAL_EVENTS } from './common/logs/events';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import { getEntryLog, OpenHelpEntryType } from './common/entryLog';
import * as PleasedApi from '@/common/api/api.pleased';
import { useApplyTheme } from '@/common/customisation/composables';
import { useLoadCustomisation } from '@/common/customisation/composables/useLoadCustomisation';
import { useCustomisationStore } from '@/common/customisation/stores/customisation.store';
import { storeToRefs } from 'pinia';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import {
  captureEventException,
  ErrorTrackingEvent,
  resolveTrackingRoute,
  setChatbotContext,
} from '@/common/error-tracking';
import { useBotUser } from './stores/useBotUser';
import router from './router';
import { useRoute } from 'vue-router/composables';
import { useConfig } from './stores/useConfig';
import { useWidgetConversation } from './stores/useWidgetConversation';
import { useMockedFlow } from './library/cf-v1/mocked-flow';
import { PleasedClient } from './pleased-sdk/PleasedClient';
import { SupportedLanguages } from './locales/languages';

// State
const showFloatButton = ref(false);
const showMyRequest = ref(false);
const fetchArticle = ref(false);
const isFromAutocomplete = ref(false);
const articleTitle = ref('');
const categoryName = ref('');
const showArticle = ref(false);
const showArticleId = ref<string | number>('');
const articleBody = ref('');
const articleSectionTitle = ref('');
const isInitialized = ref(false);
const showRouterView = ref(false);
const articleUseConfig = ref<null | ConnectToAgent>(null);
const isAwaitingJweResolution = ref(false);

// Customisation support
const customisationStore = useCustomisationStore();
// Initialize the composable with false, will enable after isInitialized becomes true
const isCustomisationEnabled = ref(false);
const { isLoading: isLoadingCustomisation, refetch } = useLoadCustomisation(isCustomisationEnabled);

const userStore = useBotUser();
const { isAuthenticated } = storeToRefs(userStore);

const configStore = useConfig();
const mockedFlow = useMockedFlow();
const widgetConversation = useWidgetConversation();

const route = useRoute();

// Initialize theme application - this sets up watchers that will automatically
// apply theme when activeTheme changes (including when documents are loaded), from customisationStore
useApplyTheme();

/**
 * Check if chatbot is in mocked/preview mode.
 * When true, all interactions are blocked via an overlay.
 */
const isMockedMode = computed<boolean>(() => {
  return Boolean(configStore.config?.initialRegistryId);
});

// Computed properties
const pageSource = computed<OpenHelpEntryType>(() => vuexStore.state.user.pageSource);

const isLocalStorageCustomisationEnabled = computed<boolean>(() => {
  if (isMockedMode.value) {
    return true;
  }

  const customisationLsKey = 'customisation-enabled';
  const customisationLsValue = localStorage.getItem(customisationLsKey);
  return customisationLsValue === 'true';
});

const hideWidgetPages = ['/conversation/'];

const isHideWidgetPage = computed<boolean>(() => {
  return hideWidgetPages.some((page) => route.path.startsWith(page));
});

const isNewChatbotFlowEnabled = computed<boolean>(() => {
  return Boolean(isLocalStorageCustomisationEnabled.value) || true;
});

/* For now we will allow new chatbot only for users who passed all of the following:
 * - Customisation feature enabled per environment
 * - Local storage customisation enabled (later change to A/B testing with % of users)
 */
const showChatWidget = computed<boolean>(() => {
  return Boolean(isNewChatbotFlowEnabled.value && !isHideWidgetPage.value);
});

const showCustomisationLoader = computed(() => {
  if (!isNewChatbotFlowEnabled.value) {
    return false;
  }

  if (!isInitialized.value || isLoadingCustomisation.value) {
    return true;
  }

  // Show loader until we have documents in the store and user is logged in
  return false;
});

// Methods
const initProject = async () => {
  // TODO: Establish one place for initial config setup

  const unmountWidgetRoot = (errorMessage: string): void => {
    logger.error('[PLEASED_MODERN_APP] Unmounting widget root because of error', { error: errorMessage });
    if (!window.VMPY) return;
    window.VMPY.$destroy();
    window.VMPY.$el?.parentNode?.removeChild(window.VMPY.$el);
  };

  try {
    const config = await CommsService.startup();

    // Set before validation so widget load failures carry the host config that caused them.
    setChatbotContext({
      clientId: config.clientId,
      widgetId: config.widgetId ?? '1',
      clientOrigin: config.clientOrigin,
      loader: 'modern',
    });

    const url = getUrlWithProtocol(config.clientId);
    // The widgetId fallback to 1 is for backward compatibility with the old chatbot
    const widgetId = Number(config.widgetId ?? '1');

    if (!url) {
      const errorMessage = 'Invalid client url specified, cannot load the chatbot';
      captureEventException(new Error(errorMessage), ErrorTrackingEvent.WidgetLoad);
      CommsService.post(CommsCommand.emit.error, errorMessage);
      unmountWidgetRoot(errorMessage);
      return;
    }

    if (Number.isNaN(widgetId)) {
      const errorMessage = 'Invalid widget id specified, cannot load the chatbot';
      captureEventException(new Error(errorMessage), ErrorTrackingEvent.WidgetLoad);
      CommsService.post(CommsCommand.emit.error, errorMessage);
      unmountWidgetRoot(errorMessage);
      return;
    }

    PleasedApi.setConfig({ url });

    try {
      const auth = useBotUser();

      const clientConfig = await PleasedApi.getChatWidgetClientConfig(widgetId, config.clientOrigin);

      if (!clientConfig.enabled) {
        const errorMessage = 'Widget is disabled';
        // Separate event from `WidgetLoad`: a disabled widget is a client-config mismatch
        // with the loader, so it can be muted in GlitchTip without hiding real load failures.
        captureEventException(new Error(errorMessage), ErrorTrackingEvent.WidgetDisabled);
        CommsService.post(CommsCommand.emit.error, errorMessage);
        unmountWidgetRoot(errorMessage);
        return;
      }

      PleasedApi.setConfig({ brandCode: clientConfig.brandSubdomain });

      // Brand is known after client-config; guest status is refined after auth resolution.
      // Route is kept in sync via bindChatbotRouteTracking; set once here for early bootstrap errors.
      setChatbotContext({
        brand: clientConfig.brandSubdomain,
        isGuest: !isAuthenticated.value,
        route: resolveTrackingRoute(route),
      });

      emitFloatButton(true);

      const payload: InitializeOptions = {
        appName: clientConfig.name,
        defaultLanguage: clientConfig.languages[0] ?? 'en',
        enabledLanguages: clientConfig.languages,
        firebaseConfig: clientConfig.firebase,
        provider: CustomerServiceProvider.PLEASED,
        brandCode: clientConfig.brandSubdomain,
        pleasedUrl: url,
        open: false,
        userId: '',
        supportJweToken: true,
        // Remove country for modern, so we can see what fails/depends on it
        // @ts-ignore
        country: '',
      };

      // Config setup for PleasedClient and configStore, new overhaul UI
      configStore.setConfig({
        ...payload,
      });
      PleasedClient.setConfig({
        url: payload.pleasedUrl,
        brandCode: payload.brandCode,
        token: payload.sporty?.token || payload.token,
        acceptLanguage: payload.defaultLanguage as SupportedLanguages,
        firebase: payload.firebaseConfig,
      });

      // Init project for the old (vuex) store and old chatbot
      // TODO: remove/move logic parts from it after migration to new UI
      vuexStore.commit('user/initProject', payload);

      // Set mocked customisation documents if provided via INIT
      if (payload.customisation?.length || payload.initialRegistryId) {
        customisationStore.setMockedDocuments(payload.customisation || []);
      }

      // Execute mocked flow immediately if initialRegistryId is provided
      // This must happen before ChatWidget renders to set up the correct UI state
      if (payload.initialRegistryId) {
        widgetConversation.resetFlow();
        mockedFlow.executeMockedFlow();
        widgetConversation.startFlow();
      }

      if (isNewChatbotFlowEnabled.value) {
        isCustomisationEnabled.value = true;

        // Skip JWE / legacy token resolution when already authenticated or widget login is in flight (LoginForm handles it).
        const skipInitAuthResolution = isAuthenticated.value || userStore.bridgeWidgetLoginInProgress;

        if (!skipInitAuthResolution) {
          isAwaitingJweResolution.value = true;
          try {
            await auth.checkUserAuthenticated();
          } catch (error) {
            PleasedApi.logErrorEvents({
              event: 'ensurePleasedUserToken',
              message: `INIT: ${(error as Error)?.message || String(error)}`,
            });
            captureEventException(error, ErrorTrackingEvent.WidgetInitAuth);
          } finally {
            isAwaitingJweResolution.value = false;
          }
        }

        setChatbotContext({
          brand: clientConfig.brandSubdomain,
          isGuest: !isAuthenticated.value,
          route: resolveTrackingRoute(route),
        });

        if (route.path === '/') {
          // replace so legacy Chatroom at `/` is not left in history behind new-home
          router.replace({ name: 'new-home' });
        }
      }

      // Set flow as initialized, but don't show router for WEB flow
      isInitialized.value = true;
      if (platform === 'wap' || platform === 'web') {
        if (payload.open) {
          // For INIT event like Sportybet web in home page, only when open Chatroom/index.vue when it is opened, to reduce API call (ex: /user/createUser, /category/list)
          showRouterView.value = true;
        }
      } else {
        // natvie
        showRouterView.value = true;
      }
    } catch (error) {
      logger.error('[MODERN_BRIDGE] Failed to fetch client-config or init project', {
        error: parseErrorMessage(error),
      });
      captureEventException(error, ErrorTrackingEvent.WidgetInit);
      CommsService.post(CommsCommand.emit.error, error);
      unmountWidgetRoot('Failed to fetch client-config or init project');
      return;
    }

    CommsService.post(CommsCommand.emit.ready);
  } catch (err) {
    captureEventException(err, ErrorTrackingEvent.WidgetBootstrap);
    CommsService.post(CommsCommand.emit.error, err);
    unmountWidgetRoot('Failed to fetch client-config or init project');
  }
};

/**
 * Handle real-time customisation updates from parent (CUSTOMISATION_UPDATE event).
 * Patches mocked documents and/or updates config (e.g. initialRegistryId) and re-runs mocked flow when needed.
 */
const handleCustomisationUpdate = (data: CustomisationUpdatePayload) => {
  if (data.customisation?.length) {
    customisationStore.patchMockedDocuments(data.customisation);
  }
  if (Object.prototype.hasOwnProperty.call(data, 'initialRegistryId')) {
    configStore.setConfig({ initialRegistryId: data.initialRegistryId });
    if (data.initialRegistryId != null) {
      widgetConversation.resetFlow();
      mockedFlow.executeMockedFlow();
      widgetConversation.startFlow();
    } else {
      mockedFlow.hideOverlayAndResetMockedFlow();
    }
  }
};

const handleCustomisationRetry = () => {
  refetch();
};

// Watchers
watch(showRouterView, (newVal) => {
  if (isMockedMode.value) {
    return;
  }

  // Analytics tracking
  if (newVal) {
    const entryLog = getEntryLog(pageSource.value);

    $pr.chatBotOpened();
    analytics.startFlushingLogs();
    analytics.track({
      name: GENERAL_EVENTS.CHATBOT_OPENED,
      metadata: {
        source: entryLog.entryType,
        url: entryLog.url,
      },
    });
  }
});

// Lifecycle - onMounted replaces created
onMounted(() => {
  Bridge.listenFromParent<CustomisationUpdatePayload>('CUSTOMISATION_UPDATE', handleCustomisationUpdate);

  initProject();

  // For dev test, uncomment all lines until onFloatButton. You can also use demo.html to serve it
  // initProject({
  //   country: Country.NG,
  //   // token:
  //   //   'patron:id:accesstoken:f4f81e5d4a8403bb33dc84c86a9b6a71g25aZ9G3Mgpu2ax0XPVwtTVpkRtaJPPo/ZTjxxa7c9jI6lTZ+iXti9JVfqKjCScIkdbqjbgyhzdBv6Q/XRYUNw==',
  //   chatStatus: '',
  //   provider: CustomerServiceProvider.PLEASED,
  //   phone: '',
  //   email: '',
  //   open: true,
  //   sporty: { country: Country.NG, currency: 'NGN' },
  //   // supportJweToken: true,
  //   userId: '7771110000',
  //   metaTags: { country: Country.NG },
  // }); // 7771110000 test1234ng

  onFloatButton((isOpen) => {
    showFloatButton.value = isOpen && (platform === 'wap' || platform === 'web'); // Only mobile web and desktop can use FloatButton
    if (isOpen === false) {
      showRouterView.value = true;

      const chatStore = (vuexStore.state as { chat: { isInitialized: boolean } }).chat;

      // If already initialized, then we need to send to redirect page
      if (chatStore.isInitialized) {
        Bridge.redirectToLastEventPage();
      }
    }
  });

  onOpenMyRequest((isOpen) => {
    showMyRequest.value = isOpen;
  });

  onOpenArticle((data) => {
    showArticle.value = data.isOpen;
    showArticleId.value = data.articleId || '';
    articleTitle.value = data.title || '';
    categoryName.value = data.categoryName || '';
    articleBody.value = data.body || '';
    articleSectionTitle.value = data.sectionTitle || '';
    fetchArticle.value = data.fetchArticle || false;
    isFromAutocomplete.value = data.isFromAutocomplete || false;
    articleUseConfig.value = data.useConfig || null;
  });

  window.addEventListener('error', (error) => {
    PleasedApi.logErrorEvents({ event: 'Window Error', message: error.message });
  });

  document.addEventListener(
    'error',
    (error) => {
      PleasedApi.logErrorEvents({ event: 'Document Error', message: error.message });
    },
    true
  );
});
</script>
